/**
 * Rasterizes the currently visible InfographicView and produces an A4 PDF.
 *
 * Two non-obvious choices:
 *
 * 1. Capture root is `[data-infographic-export-root]` (the inner wrapper
 *    inside `.canvas-body`), NOT `.canvas-body` itself. The body is the
 *    scroll container; html2canvas would clip at its viewport height. The
 *    inner wrapper has its natural full content height.
 *
 * 2. Before snapshotting, we force the root to a fixed narrow width
 *    (`TARGET_PX`). Two birds: a stable layout regardless of panel size,
 *    and a smaller source → larger destination scale (more mm per pixel
 *    when fitted to A4), so the printed result appears physically larger.
 *
 * Pagination is block-aware: we measure each top-level block's position
 * before capture and choose page-break offsets that never cut through a
 * block (chart, KPI row, text). A block taller than a page is allowed to
 * straddle, since there's no clean alternative.
 *
 * html2canvas-pro is the oklch-aware fork — required because Tailwind 4
 * uses oklch colors. Both jsPDF and html2canvas-pro are lazy-imported.
 *
 * `buildInfographicPdf` returns the blob; callers decide what to do with
 * it (trigger download / upload to workspace). `exportInfographicPdf`
 * keeps the "click → file in Downloads" path for the existing menu item.
 */

interface BlockBox {
  top: number;
  height: number;
}

const TARGET_PX = 900;
const SCALE = 2;
const MARGIN_MM = 18;

export interface InfographicPdfResult {
  blob: Blob;
  /** Filename derived from the infographic's title, with `.pdf` suffix. */
  name: string;
}

export async function buildInfographicPdf(): Promise<InfographicPdfResult | null> {
  const root = document.querySelector<HTMLElement>(
    '[data-infographic-export-root]',
  );
  if (!root) {
    console.warn('[buildInfographicPdf] No export root in DOM');
    return null;
  }

  const [{ default: html2canvas }, { default: jsPDF }] = await Promise.all([
    import('html2canvas-pro'),
    import('jspdf'),
  ]);

  const prevWidth = root.style.width;
  const prevMaxWidth = root.style.maxWidth;
  root.style.width = `${TARGET_PX}px`;
  root.style.maxWidth = `${TARGET_PX}px`;

  // Let ECharts redraw at the new width (RO → rAF → resize → paint).
  await new Promise((r) => setTimeout(r, 300));

  let canvas: HTMLCanvasElement;
  let blocks: BlockBox[];
  try {
    blocks = measureBlocks(root);
    canvas = await html2canvas(root, {
      scale: SCALE,
      backgroundColor: '#ffffff',
      useCORS: true,
      logging: false,
    });
  } finally {
    root.style.width = prevWidth;
    root.style.maxWidth = prevMaxWidth;
  }

  const pdf = new jsPDF({ unit: 'mm', format: 'a4', orientation: 'portrait' });
  const pageWidthMm = pdf.internal.pageSize.getWidth();
  const pageHeightMm = pdf.internal.pageSize.getHeight();
  const contentWidthMm = pageWidthMm - 2 * MARGIN_MM;
  const contentHeightMm = pageHeightMm - 2 * MARGIN_MM;

  const mmPerPx = contentWidthMm / canvas.width;
  const pageHeightPx = contentHeightMm / mmPerPx;

  const pageStarts = computePageStarts(blocks, pageHeightPx, canvas.height);

  for (let i = 0; i < pageStarts.length; i++) {
    const sy = pageStarts[i]!;
    const naturalEnd =
      i + 1 < pageStarts.length ? pageStarts[i + 1]! : canvas.height;
    const sh = Math.min(naturalEnd - sy, pageHeightPx);
    if (sh <= 0) continue;

    const sliceData = sliceCanvasToJpeg(canvas, sy, sh);
    if (!sliceData) continue;

    if (i > 0) pdf.addPage();
    pdf.addImage(
      sliceData,
      'JPEG',
      MARGIN_MM,
      MARGIN_MM,
      contentWidthMm,
      sh * mmPerPx,
    );
  }

  const titleEl = root.querySelector<HTMLElement>('.infographic-title');
  const baseName = (titleEl?.textContent || 'Инфографика').trim().slice(0, 80);
  return {
    blob: pdf.output('blob'),
    name: `${baseName}.pdf`,
  };
}

/** Build the PDF and trigger a browser download (Downloads folder). */
export async function exportInfographicPdf(): Promise<void> {
  const result = await buildInfographicPdf();
  if (!result) return;
  const url = URL.createObjectURL(result.blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = result.name;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

/** Measures top-level blocks (header + each child of .infographic-blocks)
 *  in canvas pixels (CSS px × SCALE) relative to the export root. */
function measureBlocks(root: HTMLElement): BlockBox[] {
  const rootRect = root.getBoundingClientRect();
  const out: BlockBox[] = [];
  const push = (el: HTMLElement | null) => {
    if (!el) return;
    const r = el.getBoundingClientRect();
    if (r.height <= 0) return;
    out.push({
      top: (r.top - rootRect.top) * SCALE,
      height: r.height * SCALE,
    });
  };
  push(root.querySelector<HTMLElement>('.infographic-header'));
  const container = root.querySelector<HTMLElement>('.infographic-blocks');
  if (container) {
    Array.from(container.children).forEach((c) => push(c as HTMLElement));
  }
  return out.sort((a, b) => a.top - b.top);
}

/**
 * Chooses page-break offsets that avoid cutting through any block. For each
 * block that would cross the current page bottom: if it fits on a single
 * page, push it to a new page starting at its top; otherwise let it straddle
 * (oversized blocks have no clean break point).
 *
 * Then inserts continuation breaks within any inter-section gap that itself
 * exceeds one page height — keeps single-page-sized chunks contiguous.
 */
function computePageStarts(
  blocks: BlockBox[],
  pageHeightPx: number,
  canvasHeight: number,
): number[] {
  const starts: number[] = [0];
  let pageBottom = pageHeightPx;

  for (const b of blocks) {
    const bottom = b.top + b.height;
    if (b.top >= pageBottom) {
      starts.push(b.top);
      pageBottom = b.top + pageHeightPx;
    } else if (bottom > pageBottom && b.height <= pageHeightPx) {
      starts.push(b.top);
      pageBottom = b.top + pageHeightPx;
    }
  }

  // Fill in continuation breaks for any section taller than a page.
  const final: number[] = [];
  for (let i = 0; i < starts.length; i++) {
    const startI = starts[i]!;
    final.push(startI);
    const nextNatural = i + 1 < starts.length ? starts[i + 1]! : canvasHeight;
    let cur = startI + pageHeightPx;
    while (cur < nextNatural) {
      final.push(cur);
      cur += pageHeightPx;
    }
  }
  return final;
}

function sliceCanvasToJpeg(
  src: HTMLCanvasElement,
  sy: number,
  sh: number,
): string | null {
  const slice = document.createElement('canvas');
  slice.width = src.width;
  slice.height = sh;
  const ctx = slice.getContext('2d');
  if (!ctx) return null;
  // Fill with white first so any transparent pixels in the source (gaps
  // between blocks) render as white in the JPEG slice — JPEG has no alpha.
  ctx.fillStyle = '#ffffff';
  ctx.fillRect(0, 0, slice.width, slice.height);
  ctx.drawImage(src, 0, sy, src.width, sh, 0, 0, src.width, sh);
  return slice.toDataURL('image/jpeg', 0.95);
}
